/** Canvas node geometry for frames: placement, size clamps, geometry patches. */
import type { Node } from "@xyflow/react";
import type { Frame } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import {
  getFrameDefaultSize,
  WIDGET_FRAME_TYPES,
  WIDGET_MIN_SIZE,
} from "@/lib/frame-defaults";
import { isFrameHiddenByMetadata } from "@/stores/frames-selectors";

type FrameNode = Node<FrameData>;

/** Where a server frame sits on the canvas. */
export type FramePlacement = Pick<
  Frame,
  "position_x" | "position_y" | "width" | "height"
>;
export type FrameGeometryPatch = Partial<FramePlacement>;

export function framePlacementOf(frame: Frame): FramePlacement {
  return {
    position_x: frame.position_x,
    position_y: frame.position_y,
    width: frame.width,
    height: frame.height,
  };
}

export const FRAME_GEOMETRY_KEYS = [
  "position_x",
  "position_y",
  "width",
  "height",
] as const;

const MIN_FRAME_WIDTH = 240;
const MIN_FRAME_HEIGHT = 120;
const MAX_FRAME_HEIGHT = 2000;
const CONVERSATION_MIN_FRAME_WIDTH = getFrameDefaultSize("note").w;

function minFrameWidth(frameType: string): number {
  return frameType === "conversation"
    ? CONVERSATION_MIN_FRAME_WIDTH
    : WIDGET_FRAME_TYPES.has(frameType)
      ? WIDGET_MIN_SIZE.w
      : MIN_FRAME_WIDTH;
}

function minFrameHeight(frameType: string): number {
  return WIDGET_FRAME_TYPES.has(frameType)
    ? WIDGET_MIN_SIZE.h
    : MIN_FRAME_HEIGHT;
}

function clampedWidth(frameType: string, width: number): number {
  return Math.max(width, minFrameWidth(frameType));
}

function clampedHeight(frameType: string, height: number): number {
  return Math.min(
    Math.max(height, minFrameHeight(frameType)),
    MAX_FRAME_HEIGHT,
  );
}

/** Effective visibility: durable `metadata.hidden` or focus-mode hiding. */
export function frameNodeHidden(
  frame: FrameData,
  focusHiddenFrameIds: ReadonlySet<string>,
): boolean {
  return (
    isFrameHiddenByMetadata(frame) || focusHiddenFrameIds.has(frame.frameId)
  );
}

/**
 * A node for a frame at its server placement. Null sizes (legacy rows) fall
 * back to the per-type default.
 */
export function frameNodeFromPlacement(
  frame: FrameData,
  placement: FramePlacement,
): FrameNode {
  const defaults = getFrameDefaultSize(frame.frameType);
  return {
    id: frame.frameId,
    type: frame.frameType,
    position: { x: placement.position_x, y: placement.position_y },
    hidden: isFrameHiddenByMetadata(frame),
    data: frame,
    draggable: frame.status !== "streaming",
    style: {
      width: clampedWidth(frame.frameType, placement.width ?? defaults.w),
      height: clampedHeight(frame.frameType, placement.height ?? defaults.h),
    },
  };
}

/** The node with `data` set to its frame and the data-derived fields updated. */
export function nodeFollowingFrame(
  node: FrameNode,
  frame: FrameData,
  focusHiddenFrameIds: ReadonlySet<string>,
): FrameNode {
  if (node.data === frame) return node;
  return {
    ...node,
    data: frame,
    hidden: frameNodeHidden(frame, focusHiddenFrameIds),
    draggable: frame.status !== "streaming",
  };
}

/** Apply a server geometry patch (position, clamped size) to a node. */
export function withFrameGeometry(
  node: FrameNode,
  geometry: FrameGeometryPatch,
): FrameNode {
  const moves =
    geometry.position_x !== undefined || geometry.position_y !== undefined;
  const resizes = geometry.width !== undefined || geometry.height !== undefined;
  if (!moves && !resizes) return node;
  const frameType = node.data.frameType;
  const position = moves
    ? {
        x: geometry.position_x ?? node.position.x,
        y: geometry.position_y ?? node.position.y,
      }
    : node.position;
  if (!resizes) return { ...node, position };
  const style = {
    ...node.style,
    ...(geometry.width !== undefined && {
      width: clampedWidth(frameType, geometry.width),
    }),
    ...(geometry.height !== undefined && {
      height: clampedHeight(frameType, geometry.height),
    }),
  };
  const defaults = getFrameDefaultSize(frameType);
  return {
    ...node,
    position,
    style,
    width: geometry.width !== undefined ? Number(style.width) : node.width,
    height: geometry.height !== undefined ? Number(style.height) : node.height,
    measured: {
      width: Number(
        style.width ?? node.measured?.width ?? node.width ?? defaults.w,
      ),
      height: Number(
        style.height ?? node.measured?.height ?? node.height ?? defaults.h,
      ),
    },
  };
}
